import React, { useEffect, useState } from 'react';
import Text from 'components/_UI/Text';
import styled, { css, keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import { FullyCentered } from 'styled/alignment/Center';
import Button from 'components/_UI/Button';
import colors from 'theme/colors';
import EstablishmentCardV2 from '../card/EstablishmentCardV2';
import { useEstablishmentsNearToYou } from 'context/establishments/EstablishmentsNearToYou';
import establishmentsApi from 'api/requests/establishments';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';

const PER_PAGE = 9;

export default function EstablishmentsNearToYou({
    wrap = false,
    onCardClick,
    searchEvents,
}) {
    const { establishmentsNearToYou, setEstablishmentsNearToYou } = useEstablishmentsNearToYou();
    const { currentLocation } = useCurrentLocation();
    const { establishmentFilter } = useEstablishmentFilter();

    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(null);

    useEffect(() => {
        getEstablishmentsNearToYou();
    }, [currentLocation?.city]);

    async function getEstablishmentsNearToYou() {
        if (!currentLocation?.city) return;
        setLoading(true);
        try {
            const { data: res } = await establishmentsApi.filter({
                city: currentLocation?.city,
                state: currentLocation?.state,
                limit: PER_PAGE,
                page: 1,
            });
            setEstablishmentsNearToYou(res?.data || []);
            setTotalCount(res?.meta?.total ?? (res?.data?.length || 0));
        } catch (error) {
            console.error('Error fetching establishments near to you:', error);
        } finally {
            setLoading(false);
        }
    }

    const displayedItems = establishmentFilter?.categories?.length
        ? (establishmentsNearToYou || []).filter(est =>
            est.tagsEstablishments?.some(t => establishmentFilter.categories.includes(t.name))
          )
        : establishmentsNearToYou;

    return (
        <NearToYouContainer>
            {!establishmentsNearToYou || loading ? (
                <SkeletonContainer>
                    {[1, 2, 3, 4, 5, 6].map((_, i) => (
                        <EventCardSkeleton key={i} />
                    ))}
                </SkeletonContainer>
            ) : establishmentsNearToYou?.length === 0 ? (
                <ContainerNotEstablishments>
                    <ContentNotEstablishments>
                        <Column verticalCenter>
                            <Text name='default' type='bold' text-align='center'>
                                Não encontramos nenhum estabelecimento próximo a você
                            </Text>
                            <ContainerButtonSearch>
                                <Button id={'not-found-events-button'} onClick={() => searchEvents()}>
                                    Procurar estabelecimentos
                                </Button>
                            </ContainerButtonSearch>
                        </Column>
                    </ContentNotEstablishments>
                </ContainerNotEstablishments>
            ) : (
                <>
                    <FullyCentered>
                        <Text name='small' type='bold'>
                            {totalCount === 1
                                ? '1 estabelecimento perto de você'
                                : `${totalCount} estabelecimentos perto de você`
                            }
                        </Text>
                    </FullyCentered>

                    <CardsContainer wrapContent={wrap}>
                        {displayedItems?.map((establishment, index) => (
                            <AnimatedCard key={establishment.slug} $index={index}>
                                <EstablishmentCardV2
                                    whiteBackground={true}
                                    data={establishment}
                                    onCardClick={() => onCardClick(establishment)}
                                />
                            </AnimatedCard>
                        ))}
                    </CardsContainer>

                </>
            )}
        </NearToYouContainer>
    );
}

const NearToYouContainer = styled(Column)`
    margin-top: 16px;
    @media screen and (max-width: ${breakpoints.xl}) {
        display: flex;
        justify-content: center;
    }
`;

const CardsContainer = styled(Row)`
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    ${({ wrapContent }) => wrapContent && css`
        overflow-x: unset;
        overflow-y: unset;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-around;
        height: auto;

        & > div {
            min-width: unset;
            width: auto;
            margin-left: 0;
        }
    `}

    @media screen and (min-width: ${breakpoints.lg}) {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        overflow-x: hidden;
        padding-bottom: 0;
        align-items: start;

        & > div {
            width: 100%;
            min-width: 0;
            margin: 0;
        }
    }
`;

const ContainerNotEstablishments = styled(Column)`
    width: 100%;
    @media screen and (min-width: ${breakpoints.xm}) and (max-width: ${breakpoints.lg}) {
        display: flex;
        align-items: center;
        height: 180px;
        justify-content: center;
    }
`;

const ContentNotEstablishments = styled(FullyCentered)`
    width: 100%;
    text-align: center;
    align-items: center;
    display: flex;
`;

const ContainerButtonSearch = styled(FullyCentered)`
    width: 260px;
`;


const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const AnimatedCard = styled.div`
    width: 100%;
    animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 50, 300)}ms;
`;

const SkeletonContainer = styled(Row)`
    margin-top: 16px;
    flex-wrap: nowrap;
    overflow-x: hidden;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        overflow-x: unset;
        margin-top: 30px;

        & > div {
            width: 31.5%;
            min-width: unset;
            margin-left: 1.5%;
            margin-top: 20px;
            & > div {
                width: 100%;
            }
        }

        & > div:first-child {
            margin-left: 1.5%;
        }
    }
`;
